import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, AlertTitle, Box, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormCheckbox, FormSlider, FormSwitch, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormDatePicker } from "@zfp-akademie-a-s/zfp-design/form/date-picker";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { calculateMonthlyContributionWithFee, calculateMonthlyContributionWithoutFee } from "Charts/Utils/CommonChartUtils";
import { type CalculateMonthlyContributionParams, calculateMonthlyContribution } from "Charts/Utils/FinancialReserveChartUtils";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { type FinancialReserveGoalForm, financialReserveFormSchema, minDate } from "Forms/Schemas/Goals/financialReserveFormSchema";
import {
  getGetAnalysisGoalsQueryKey,
  getGetFinancialReserveGoalQueryKey,
  useCreateFinancialReserveGoal,
  useUpdateFinancialReserveGoal,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type FinancialReserveGoalDto, FinancialReserveGoalDtoPriorityLevel } from "Infrastructure/ApiZfp/Schema";
import { DEFAULT_FEE_RATE, DEFAULT_INFLATION_RATE, INTEREST_RATE_SAVINGS } from "Infrastructure/Constants/financialConstants";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { addMonths, differenceInMonths, format, isBefore, startOfDay } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { FinancialReserveChart } from "./FinancialReserveChart";
import { ModelingAccordion } from "./ModelingAccordion";

type Props = {
  readonly onClose: () => void;
  readonly editGoal?: FinancialReserveGoalDto;
  readonly goalId?: string;
};

export const FinancialReserveForm = forwardRef<{}, Props>(({ onClose, editGoal, goalId }, ref) => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const { readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const readOnly = isAnalysisReadOnly || isInvestmentCaseLocked;

  const defaultValues = useMemo(() => {
    if (editGoal) {
      const withFee = Number.isFinite(editGoal.modeling.investmentAmount?.value) ? editGoal.modeling.investmentAmount?.value : 0;
      const fee = Number.isFinite(editGoal.modeling.fee?.rate) ? editGoal.modeling.fee?.rate : 0;
      const withoutFee = calculateMonthlyContributionWithoutFee(withFee as number, fee as number);

      return {
        name: editGoal.name,
        includeFee: editGoal.modeling.fee?.enabled,
        includeInflation: editGoal.modeling.inflation?.enabled,
        inflationRate: Number.isFinite(editGoal.modeling.inflation?.rate) ? (editGoal.modeling.inflation?.rate as any) : 0,
        feeRate: Number.isFinite(editGoal.modeling.fee?.rate) ? (editGoal.modeling.fee?.rate as any) : 0,
        interestRate: Number.isFinite(editGoal.modeling.appreciationRate) ? (editGoal.modeling.appreciationRate as any) : 0,
        currentAmount: Number.isFinite(editGoal.data.currentFinancialReserve?.value)
          ? (editGoal.data.currentFinancialReserve?.value as any)
          : 0,
        targetAmount: Number.isFinite(editGoal.data.requiredFinancialReserve?.value)
          ? (editGoal.data.requiredFinancialReserve?.value as any)
          : 0,
        targetDate: new Date(editGoal.data.targetDate as string),
        monthlyContribution: Number.isFinite(withoutFee) ? (withoutFee as any) : 0,
        monthlyContributionWithFee: Number.isFinite(withFee) ? (withFee as any) : 0,
        note: editGoal.note || "",
        priority: editGoal.priorityLevel === FinancialReserveGoalDtoPriorityLevel.high,
        referenceStartDate: editGoal.referenceStartDate ? new Date(editGoal.referenceStartDate) : new Date(),
      };
    }

    return {
      name: t(Resources.AnalysisDetail.Goals.GoalType.FinancialReserve),
      includeFee: true,
      includeInflation: true,
      feeRate: DEFAULT_FEE_RATE,
      inflationRate: DEFAULT_INFLATION_RATE,
      interestRate: INTEREST_RATE_SAVINGS,
      currentAmount: 0,
      targetAmount: 0,
      targetDate: addMonths(new Date(), 12),
      monthlyContribution: 0,
      monthlyContributionWithFee: 0,
      referenceStartDate: new Date(),
    };
  }, [editGoal, t]);

  const methods = useForm<FinancialReserveGoalForm>({
    resolver: yupResolver(financialReserveFormSchema(t)),
    mode: "onBlur",
    defaultValues,
  });

  const { control, setValue, watch, handleSubmit, clearErrors } = methods;

  const currentAmount = watch("currentAmount");
  const targetAmount = watch("targetAmount");
  const targetDate = watch("targetDate");
  const monthlyContribution = watch("monthlyContribution");
  const monthlyContributionWithFee = watch("monthlyContributionWithFee");
  const includeInflation = watch("includeInflation");
  const inflationRate = watch("inflationRate");
  const interestRate = watch("interestRate");
  const includeFee = watch("includeFee");
  const feeRate = watch("feeRate");
  const referenceStartDate = watch("referenceStartDate");

  const isInvalidTargetDate = isBefore(startOfDay(targetDate), startOfDay(minDate));

  const shouldRenderChart =
    targetDate &&
    !isInvalidTargetDate &&
    monthlyContribution > 0 &&
    targetAmount > 0 &&
    Number.isFinite(Number(monthlyContribution)) &&
    Number.isFinite(Number(targetAmount)) &&
    Number.isFinite(Number(currentAmount)) &&
    Number.isFinite(Number(interestRate)) &&
    Number.isFinite(Number(inflationRate));

  const { mutate: createFinancialReserveGoal } = useCreateFinancialReserveGoal();

  const { mutate: updateFinancialReserveGoal } = useUpdateFinancialReserveGoal();

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (editGoal) {
        handleSubmit(onEditGoal)();
      } else {
        handleSubmit(onCreateGoal)();
      }
    },
  }));

  const onChangeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isEditingTargetAmountRef = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      if (onChangeTimeoutRef.current) {
        clearTimeout(onChangeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!targetDate || isInvalidTargetDate) return;
    clearErrors("targetDate");
    onChangeTargetDate();
  }, [isInvalidTargetDate, targetDate, clearErrors]);

  const onChangeTargetDate = () => {
    onChange({
      currentAmount: parseNumber(currentAmount),
      durationInMonths: targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1,
      targetAmount: parseNumber(targetAmount),
      inflationRate,
      interestRate,
    });
  };

  const onChange = ({
    currentAmount,
    inflationRate,
    interestRate,
    targetAmount,
    durationInMonths,
  }: CalculateMonthlyContributionParams & { currentAmount: number }) => {
    if (onChangeTimeoutRef.current) {
      clearTimeout(onChangeTimeoutRef.current);
    }

    onChangeTimeoutRef.current = setTimeout(() => {
      if (interestRate === undefined || targetAmount === undefined || durationInMonths === undefined) {
        return;
      }

      if (durationInMonths <= 0 || targetAmount < 0 || currentAmount < 0) {
        return;
      }

      if (interestRate !== undefined && targetAmount !== undefined) {
        const monthlyContribution = calculateMonthlyContribution({
          currentAmount: Number(currentAmount) || 0,
          targetAmount: Number(targetAmount) || 0,
          durationInMonths: Number(durationInMonths || 1),
          interestRate: Number(interestRate) || 0,
          inflationRate: Number(inflationRate) || 0,
        });

        if (Number.isFinite(monthlyContribution) && !Number.isNaN(monthlyContribution)) {
          setValue("monthlyContribution", monthlyContribution as any);

          const newContributionWithFee = calculateMonthlyContributionWithFee(monthlyContribution, parseNumber(feeRate) || 0);
          if (Number.isFinite(newContributionWithFee) && !Number.isNaN(newContributionWithFee)) {
            setValue("monthlyContributionWithFee", newContributionWithFee as any);
          }
        }
      }
    }, 100);
  };

  const parseNumber = (value: unknown): number => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === "string") {
      const normalized = value.replace(/\s/g, "").replace(/,/g, ".");
      const n = Number(normalized);
      return Number.isFinite(n) && !Number.isNaN(n) ? n : 0;
    }
    return 0;
  };

  const onMonthlyContributionChange = (newContributionWithFee?: unknown) => {
    const contribution = newContributionWithFee || monthlyContributionWithFee;
    const monthlyContributionWithoutFee = calculateMonthlyContributionWithoutFee(parseNumber(contribution) || 0, parseNumber(feeRate) || 0);

    if (Number.isFinite(monthlyContributionWithoutFee) && !Number.isNaN(monthlyContributionWithoutFee)) {
      setValue("monthlyContribution", monthlyContributionWithoutFee as any);

      const newContributionWithFeeNumber = parseNumber(contribution as any) || 0;
      if (Number.isFinite(newContributionWithFeeNumber) && !Number.isNaN(newContributionWithFeeNumber)) {
        setValue("monthlyContributionWithFee", newContributionWithFeeNumber as any);
      }

      // Recalculate targetAmount based on the updated monthly contribution
      const months = targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1;
      const inflationUsed = includeInflation ? parseNumber(inflationRate) || 0 : 0;
      const monthlyCompoundRate = (parseNumber(interestRate) - Number(inflationUsed)) / 100 / 12;
      const startAmount = parseNumber(currentAmount) || 0;

      let recalculatedTargetAmount: number;
      if (Math.abs(monthlyCompoundRate) < 1e-10) {
        recalculatedTargetAmount = startAmount + Number(monthlyContributionWithoutFee) * months;
      } else {
        const pow = (1 + monthlyCompoundRate) ** months;
        recalculatedTargetAmount = startAmount * pow + Number(monthlyContributionWithoutFee) * ((-1 + pow) / monthlyCompoundRate);
      }

      if (Number.isFinite(recalculatedTargetAmount) && !Number.isNaN(recalculatedTargetAmount)) {
        if (!isEditingTargetAmountRef.current) {
          setValue("targetAmount", Math.max(0, Math.round(recalculatedTargetAmount)) as any);
        }
      }
    }
  };

  const onCreateGoal = (data: FinancialReserveGoalForm) => {
    if (onChangeTimeoutRef.current) {
      clearTimeout(onChangeTimeoutRef.current);
    }

    const withFeeContribution = parseNumber(data.monthlyContributionWithFee);

    createFinancialReserveGoal(
      {
        analysisId,
        versionId,
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? FinancialReserveGoalDtoPriorityLevel.high : FinancialReserveGoalDtoPriorityLevel.low,
          data: {
            currentFinancialReserve: {
              value: parseNumber(data.currentAmount),
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            requiredFinancialReserve: {
              value: parseNumber(data.targetAmount),
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            targetDate: format(data.targetDate, "yyyy-MM-dd"),
          },
          modeling: {
            investmentAmount: {
              value: Number.isFinite(Number(withFeeContribution)) ? Number(withFeeContribution) : 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            inflation: {
              enabled: !!data.includeInflation,
              rate: parseNumber(data.inflationRate),
            },
            fee: {
              enabled: !!data.includeFee,
              rate: parseNumber(data.feeRate),
            },
            appreciationRate: parseNumber(data.interestRate),
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalCreated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  const onEditGoal = (data: FinancialReserveGoalForm) => {
    if (onChangeTimeoutRef.current) {
      clearTimeout(onChangeTimeoutRef.current);
    }

    const withFeeContribution = parseNumber(data.monthlyContributionWithFee);

    updateFinancialReserveGoal(
      {
        analysisId,
        versionId,
        goalId: editGoal?.goalId ?? "",
        data: {
          name: data.name,
          note: data.note || "",
          priorityLevel: data.priority ? FinancialReserveGoalDtoPriorityLevel.high : FinancialReserveGoalDtoPriorityLevel.low,
          data: {
            targetDate: format(data.targetDate, "yyyy-MM-dd"),
            currentFinancialReserve: {
              value: parseNumber(data.currentAmount),
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
            requiredFinancialReserve: {
              value: parseNumber(data.targetAmount),
              currencyCode: "CZK",
              frequency: AmountFrequency.one_time,
            },
          },
          modeling: {
            inflation: {
              enabled: !!data.includeInflation,
              rate: parseNumber(data.inflationRate),
            },
            investmentAmount: {
              value: Number.isFinite(Number(withFeeContribution)) ? Number(withFeeContribution) : 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            fee: {
              enabled: !!data.includeFee,
              rate: parseNumber(data.feeRate),
            },
            appreciationRate: parseNumber(data.interestRate),
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(t(Resources.AnalysisDetail.Toast.GoalUpdated));
          onClose();
          queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
          queryClient.invalidateQueries({
            queryKey: getGetFinancialReserveGoalQueryKey(analysisId, versionId, goalId ?? editGoal?.goalId ?? undefined),
          });
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Toast.GoalError));
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Stack spacing={Spacing.XXL}>
          <Box>
            <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
              {t(Resources.AnalysisDetail.Goals.GoalType.FinancialReserveLong)}
            </Typography>
            <Grid container spacing={Spacing.XL}>
              <Grid size={12}>
                <FormCheckbox
                  control={control}
                  name="priority"
                  label={t(Resources.AnalysisDetail.Goals.GoalPriorityLabel)}
                  disabled={readOnly}
                />
              </Grid>
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="name"
                  label={t(Resources.AnalysisDetail.Goals.GoalName)}
                  required
                  disabled={readOnly}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <FormNumberField
                  control={control}
                  name="currentAmount"
                  label={t(Resources.AnalysisDetail.Goals.CurrentEmergencyFundAmount)}
                  required
                  scale={0}
                  min={0}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly}
                  onBlur={() =>
                    onChange({
                      currentAmount: parseNumber(currentAmount),
                      durationInMonths: targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1,
                      targetAmount,
                      inflationRate,
                      interestRate,
                    })
                  }
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <FormNumberField
                  control={control}
                  name="targetAmount"
                  label={t(Resources.AnalysisDetail.Goals.TargetEmergencyFundAmount)}
                  required
                  scale={0}
                  thousandsSeparator=" "
                  unit={t(Resources.Common.CZKSuffix)}
                  disabled={readOnly}
                  onBlur={() => {
                    isEditingTargetAmountRef.current = false;
                    onChange({
                      currentAmount: parseNumber(currentAmount),
                      durationInMonths: targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1,
                      targetAmount: parseNumber(targetAmount),
                      inflationRate,
                      interestRate,
                    });
                  }}
                  onFocus={() => {
                    isEditingTargetAmountRef.current = true;
                  }}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <FormDatePicker
                  control={control}
                  name="targetDate"
                  label={t(Resources.AnalysisDetail.Goals.PurchaseDate)}
                  required
                  minDate={minDate}
                  disabled={readOnly}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid size={12}>
                <Alert severity="info">
                  <AlertTitle>{t(Resources.AnalysisDetail.Goals.RecommendedEmergencyFundAmount)}</AlertTitle>
                  {t(Resources.AnalysisDetail.Goals.RecommendedEmergencyFundAmountHint)}
                </Alert>
              </Grid>
            </Grid>
          </Box>
          {targetDate && targetAmount > 0 && (
            <Box>
              <ModelingAccordion>
                <Grid container spacing={Spacing.XL} mt={Spacing.XL}>
                  <Grid size={12}>
                    <FormSlider
                      control={control}
                      disabled={readOnly}
                      name="monthlyContributionWithFee"
                      min={0}
                      max={50_000}
                      valueType="text"
                      minLabel={formatCurrency(0, CurrencyCode.CZK)}
                      maxLabel={formatCurrency(50_000, CurrencyCode.CZK)}
                      ariaLabel={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      onChange={(value) => onMonthlyContributionChange(value.toString())}
                    />
                  </Grid>
                  <Grid size={8}>
                    <FormNumberField
                      control={control}
                      name="monthlyContributionWithFee"
                      label={t(Resources.AnalysisDetail.Goals.MonthlyContribution)}
                      required
                      scale={2}
                      min={0}
                      thousandsSeparator=" "
                      radix=","
                      mapToRadix={["."]}
                      unit={t(Resources.Common.CZKSuffix)}
                      disabled={readOnly}
                      onBlur={() => onMonthlyContributionChange()}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={4}>
                    <FormNumberField
                      control={control}
                      name="interestRate"
                      label={t(Resources.AnalysisDetail.Goals.InterestRate)}
                      required
                      scale={1}
                      min={0}
                      thousandsSeparator=""
                      radix=","
                      mapToRadix={["."]}
                      unit="%"
                      disabled={readOnly}
                      onChange={(newInterestRate) =>
                        onChange({
                          durationInMonths: Math.max(1, differenceInMonths(targetDate, referenceStartDate)),
                          targetAmount: Number(targetAmount),
                          inflationRate,
                          interestRate: Number(newInterestRate),
                          currentAmount,
                        })
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      name="includeInflation"
                      label={t(Resources.AnalysisDetail.Goals.IncludeInflation)}
                      disabled={readOnly}
                      onChange={(checked) => {
                        const newInflationRate = checked ? DEFAULT_INFLATION_RATE : 0;

                        if (newInflationRate === Number(interestRate)) {
                          setValue("inflationRate", (newInflationRate + 0.1) as any);
                        } else {
                          setValue("inflationRate", newInflationRate as any);
                        }

                        onChange({
                          durationInMonths: targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1,
                          targetAmount: Number(targetAmount),
                          inflationRate: newInflationRate,
                          interestRate,
                          currentAmount,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="inflationRate"
                      scale={1}
                      min={0}
                      max={20}
                      thousandsSeparator=""
                      radix=","
                      mapToRadix={["."]}
                      unit="%"
                      disabled={!includeInflation || readOnly}
                      onChange={(newInflationRate) => {
                        onChange({
                          durationInMonths: targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1,
                          targetAmount: Number(targetAmount),
                          inflationRate: includeInflation ? Number(newInflationRate) : 0,
                          interestRate,
                          currentAmount,
                        });
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={9} display="flex" alignItems="center">
                    <FormSwitch
                      control={control}
                      name="includeFee"
                      label={t(Resources.AnalysisDetail.Goals.IncludeFee)}
                      disabled={readOnly}
                      onChange={(checked) => {
                        const newFeeRate = checked ? DEFAULT_FEE_RATE : 0;
                        const currentMonthlyContribution = parseNumber(monthlyContribution) || 0;
                        const newContributionWithFee = calculateMonthlyContributionWithFee(currentMonthlyContribution, newFeeRate);

                        setValue("feeRate", newFeeRate as any);

                        if (Number.isFinite(newContributionWithFee) && !Number.isNaN(newContributionWithFee)) {
                          setValue("monthlyContributionWithFee", newContributionWithFee as any);

                          // Recalculate targetAmount based on the updated monthly contribution
                          const months = targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1;
                          const inflationUsed = includeInflation ? parseNumber(inflationRate) || 0 : 0;
                          const monthlyCompoundRate = (parseNumber(interestRate) - Number(inflationUsed)) / 100 / 12;
                          const startAmount = parseNumber(currentAmount) || 0;

                          let recalculatedTargetAmount: number;
                          if (Math.abs(monthlyCompoundRate) < 1e-10) {
                            recalculatedTargetAmount = startAmount + Number(currentMonthlyContribution) * months;
                          } else {
                            const pow = (1 + monthlyCompoundRate) ** months;
                            recalculatedTargetAmount =
                              startAmount * pow + Number(currentMonthlyContribution) * ((-1 + pow) / monthlyCompoundRate);
                          }

                          if (Number.isFinite(recalculatedTargetAmount) && !Number.isNaN(recalculatedTargetAmount)) {
                            if (!isEditingTargetAmountRef.current) {
                              setValue("targetAmount", Math.max(0, Math.round(recalculatedTargetAmount)) as any);
                            }
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={3}>
                    <FormNumberField
                      control={control}
                      name="feeRate"
                      scale={1}
                      min={0}
                      max={20}
                      thousandsSeparator=""
                      radix=","
                      mapToRadix={["."]}
                      unit="%"
                      disabled={!includeFee || readOnly}
                      onChange={(newFeeRate) => {
                        const currentMonthlyContribution = parseNumber(monthlyContribution) || 0;
                        const newFeeRateNumber = parseNumber(newFeeRate as any) || 0;
                        const newContributionWithFee = calculateMonthlyContributionWithFee(currentMonthlyContribution, newFeeRateNumber);

                        if (Number.isFinite(newContributionWithFee) && !Number.isNaN(newContributionWithFee)) {
                          setValue("monthlyContributionWithFee", newContributionWithFee as any);

                          // Recalculate targetAmount based on the updated monthly contribution
                          const months = targetDate ? Math.max(1, differenceInMonths(targetDate, referenceStartDate)) : 1;
                          const inflationUsed = includeInflation ? parseNumber(inflationRate) || 0 : 0;
                          const monthlyCompoundRate = (parseNumber(interestRate) - Number(inflationUsed)) / 100 / 12;
                          const startAmount = parseNumber(currentAmount) || 0;

                          let recalculatedTargetAmount: number;
                          if (Math.abs(monthlyCompoundRate) < 1e-10) {
                            recalculatedTargetAmount = startAmount + Number(currentMonthlyContribution) * months;
                          } else {
                            const pow = (1 + monthlyCompoundRate) ** months;
                            recalculatedTargetAmount =
                              startAmount * pow + Number(currentMonthlyContribution) * ((-1 + pow) / monthlyCompoundRate);
                          }

                          if (Number.isFinite(recalculatedTargetAmount) && !Number.isNaN(recalculatedTargetAmount)) {
                            if (!isEditingTargetAmountRef.current) {
                              setValue("targetAmount", Math.max(0, Math.round(recalculatedTargetAmount)) as any);
                            }
                          }
                        }
                      }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </ModelingAccordion>
            </Box>
          )}
          <Box>
            <Grid container spacing={Spacing.XL}>
              {shouldRenderChart && (
                <Grid size={12}>
                  <Box padding={Spacing.S} display="flex" justifyContent="space-around" alignItems="center">
                    <Stack spacing={Spacing.XS} display="flex" justifyContent="center" alignItems="center">
                      <Typography variant="caption" fontWeight={500}>
                        {t(Resources.AnalysisDetail.Goals.EmergencyFundCreation)}
                      </Typography>
                      <Typography variant="caption">
                        {formatCurrency(Math.round(Number(monthlyContributionWithFee)), CurrencyCode.CZK)}/{t(Resources.Common.Month)}
                      </Typography>
                    </Stack>
                    <Stack spacing={Spacing.XS} display="flex" justifyContent="center" alignItems="center">
                      <Typography variant="caption" fontWeight={500}>
                        {t(Resources.AnalysisDetail.Goals.EmergencyFundAppreciation)}
                      </Typography>
                      <Typography variant="caption" color="transparent">
                        /
                      </Typography>
                    </Stack>
                  </Box>
                  <FinancialReserveChart
                    currentAmount={Number.isFinite(Number(currentAmount)) ? Number(currentAmount) : 0}
                    targetAmount={Number.isFinite(Number(targetAmount)) ? Number(targetAmount) : 0}
                    targetDate={targetDate}
                    referenceStartDate={referenceStartDate}
                    monthlyContributionAmount={Number.isFinite(Number(monthlyContribution)) ? Number(monthlyContribution) : 0}
                    inflationRate={includeInflation && Number.isFinite(Number(inflationRate)) ? Number(inflationRate) : 0}
                    interestRate={Number.isFinite(Number(interestRate)) ? Number(interestRate) : 0}
                  />
                </Grid>
              )}
              <Grid size={12}>
                <FormTextField
                  control={control}
                  name="note"
                  label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                  maxLength={NOTE_MAX_LENGTH}
                  disabled={readOnly}
                  multiline
                  minRows={6}
                  maxRows={8}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
});
